import { round } from "lodash"

import { Dayjs } from "utils/dayjs"
import i18n from "intl/i18n"
import TenantInvoicePosition, { InvoicePositionDistributionRule, InvoicePositionType } from "./TenantInvoicePosition"
import { getTranslation } from "utils/translations"
import { Currency } from "config/constants"

export const INVOICE_POSITION_UNIT_PRICE_MAX = 999999999.99
export const INVOICE_POSITION_UNIT_QUANTITY_MAX = 9999999

export enum InvoicePositionProcessingType {
    withNextInvoice = "withNextInvoice",
    withLastInvoice = "withLastInvoice",
    afterSpecificDate = "afterSpecificDate",
}

export enum InvoicePositionUnit {
    month = "month",
    week = "week",
    day = "day",
    price = "price",
    liters = "liters",
    hours = "hours",
    kilometers = "kilometers",
}

export interface IInvoicePositionUnitPrice {
    unit?: InvoicePositionUnit | null
    unitPrice?: number | null
    unitQuantity?: number | null
}

export interface IGetInvoicePositionBaseDto extends IInvoicePositionUnitPrice {
    type?: InvoicePositionType | null
    number: string
    text?: string | null
    distributionRule: InvoicePositionDistributionRule
}

export default class InvoicePositionBase {
    type?: InvoicePositionType
    number: string
    customText?: string
    distributionRule: InvoicePositionDistributionRule
    unit?: InvoicePositionUnit
    unitPrice?: number
    unitQuantity?: number

    // hydrated separetely
    invoicePosition?: TenantInvoicePosition

    constructor(dto: IGetInvoicePositionBaseDto) {
        this.type = dto.type || undefined
        this.number = dto.number
        this.customText = typeof dto.text === "string" ? dto.text : undefined
        this.distributionRule = dto.distributionRule

        this.unit = dto.unit || undefined
        this.unitPrice = dto.unitPrice ?? undefined
        this.unitQuantity = dto.unitQuantity ?? undefined
    }

    get isHydrated() {
        return !!this.invoicePosition
    }

    get description(): string {
        if (!this.invoicePosition?.description) return ""
        return getTranslation(this.invoicePosition.description)
    }

    get text(): string {
        if (typeof this.customText === "string") return this.customText
        if (!this.invoicePosition?.text) return ""
        return getTranslation(this.invoicePosition.text)
    }

    set text(text: string | null | undefined) {
        this.customText = text || ""
    }
}

export function getInvoicePositionTypeLabel(invoicePositionType: InvoicePositionType) {
    return {
        [InvoicePositionType.single]: i18n.t('reservationInvoicePositionType.single'),
        [InvoicePositionType.recurring]: i18n.t('reservationInvoicePositionType.recurring'),
    }[invoicePositionType] || invoicePositionType
}

export function getInvoicePositionUnitLabel(invoicePositionUnit: InvoicePositionUnit, currency?: Currency | null) {
    const unitLabel: string | undefined = {
        [InvoicePositionUnit.month]: i18n.t('reservationInvoicePositionUnit.month'),
        [InvoicePositionUnit.week]: i18n.t('reservationInvoicePositionUnit.week'),
        [InvoicePositionUnit.day]: i18n.t('reservationInvoicePositionUnit.day'),
        [InvoicePositionUnit.liters]: i18n.t('reservationInvoicePositionUnit.liters'),
        [InvoicePositionUnit.hours]: i18n.t('reservationInvoicePositionUnit.hours'),
        [InvoicePositionUnit.kilometers]: i18n.t('reservationInvoicePositionUnit.kilometers'),
    }[invoicePositionUnit]

    if (!currency) {
        return unitLabel || invoicePositionUnit
    }

    return `${currency} ${unitLabel ? ` / ${unitLabel}` : ""}`
}


export function isQuantityRequiredForInvoicePositionUnit(unit: InvoicePositionUnit) {
    return unit === InvoicePositionUnit.liters
        || unit === InvoicePositionUnit.hours
        || unit === InvoicePositionUnit.kilometers
}

export function getInvoicePositionQuantityStaticLabel(unit: InvoicePositionUnit, quantity?: number | null, { showRecurringAs1Month }: { showRecurringAs1Month?: boolean } = {}) {
    switch (unit) {
        case InvoicePositionUnit.month:
            return i18n.t('common.xMonths', { count: showRecurringAs1Month ? 1 : (quantity || 0) })
        case InvoicePositionUnit.week:
            return showRecurringAs1Month
                ? i18n.t('common.xMonths', { count: 1 })
                : i18n.t('common.xWeeks', { count: quantity || 0 })
        case InvoicePositionUnit.day:
            return showRecurringAs1Month
                ? i18n.t('common.xMonths', { count: 1 })
                : i18n.t('common.xDays', { count: quantity || 0 })
        case InvoicePositionUnit.liters:
            return `${quantity || 0} ${i18n.t('reservationInvoicePositionUnit.liters')}`
        case InvoicePositionUnit.hours:
            return `${quantity || 0} ${i18n.t('reservationInvoicePositionUnit.hours')}`
        case InvoicePositionUnit.kilometers:
            return `${quantity || 0} ${i18n.t('reservationInvoicePositionUnit.kilometers')}`
    }
}

export function calculateInvoicePositionAmount({
    unit,
    unitPrice,
    unitQuantity,
    workingDaysPerMonth,
}: {
    unit: InvoicePositionUnit | undefined
    unitPrice: number | undefined
    unitQuantity: number | undefined
    workingDaysPerMonth?: number | null
}): number | null {
    if (!unit
        || typeof unitPrice !== "number"
        || (isQuantityRequiredForInvoicePositionUnit(unit) && typeof unitQuantity !== "number")) {
        return null
    }

    let amount: number | null
    switch (unit) {
        case InvoicePositionUnit.month:
        case InvoicePositionUnit.price:
            amount = unitPrice
            break
        case InvoicePositionUnit.week:
            amount = unitPrice / 7 * (workingDaysPerMonth ?? 30)
            break
        case InvoicePositionUnit.day:
            amount = unitPrice * (workingDaysPerMonth ?? 30)
            break
        case InvoicePositionUnit.liters:
        case InvoicePositionUnit.hours:
        case InvoicePositionUnit.kilometers:
            amount = typeof unitQuantity === "number" ? unitPrice * unitQuantity : null
            break
    }

    return amount === null
        ? null
        : round(amount, 2)
}



export function getInvoicePositionDates({
    isSale,
    invoicePositionStart,
    invoicePositionEnd,
    invoicePositionType,
    invoicePositionProcessingType,
    reservationStart,
    reservationEnd,
    lastNotCreditedInvoiceEndDate,
}: {
    isSale: boolean
    invoicePositionStart: Dayjs | null
    invoicePositionEnd: Dayjs | null
    invoicePositionType: InvoicePositionType | null
    invoicePositionProcessingType: InvoicePositionProcessingType | null
    reservationStart: Dayjs
    reservationEnd: Dayjs | null
    lastNotCreditedInvoiceEndDate: Dayjs | null
}): string[] {
    const lastNotCreditedInvoiceEndDateNextDay = lastNotCreditedInvoiceEndDate?.clone().add(1, "day")

    const dates: string[] = []
    if (isSale) {
        if (invoicePositionStart) {
            dates.push(invoicePositionStart.format(i18n.t("config.dateLongFormat")))
        }
    } else {
        if (invoicePositionType === InvoicePositionType.recurring) {
            if (invoicePositionStart) {
                dates.push(invoicePositionStart.format(i18n.t("config.dateLongFormat")))
            } else if (invoicePositionEnd) {
                // show reservation start instead of invoice position start if only invoice position end is set
                dates.push(reservationStart.format(i18n.t("config.dateLongFormat")))
            }

            if (reservationEnd) {
                if (invoicePositionEnd) {
                    dates.push(invoicePositionEnd.format(i18n.t("config.dateLongFormat")))
                } else if (invoicePositionStart) {
                    // show reservation end instead of invoice position end if only invoice position start is set
                    dates.push(reservationEnd.format(i18n.t("config.dateLongFormat")))
                }
            }
        } else if (invoicePositionType === InvoicePositionType.single) {
            if (invoicePositionProcessingType === InvoicePositionProcessingType.withNextInvoice) {
                dates.push((lastNotCreditedInvoiceEndDateNextDay || reservationStart).format(i18n.t("config.dateLongFormat")))
            } else if (invoicePositionProcessingType === InvoicePositionProcessingType.withLastInvoice) {
                if (reservationEnd) {
                    dates.push(reservationEnd.format(i18n.t("config.dateLongFormat")))
                }
            } else if (invoicePositionProcessingType === InvoicePositionProcessingType.afterSpecificDate) {
                dates.push((invoicePositionStart || reservationStart).format(i18n.t("config.dateLongFormat")))
            }
        }
    }
    return dates
}
